import { buildFieldAnchors } from '../shared/previewFieldAnchors';

/**
 * Type and layout metrics for the official আবেদনকারীর বিশেষ তথ্য ফরম
 * (ফরম-২ এর অতিরিক্ত তথ্য) — the supplementary sheet the special committee
 * attaches to Form-2.
 *
 * These follow the latest amended revision of the form (the gazette sheet that
 * ends «(৪) ফরম-১১ বিলুপ্ত হইবে।»), which differs from the revision this tool
 * was first built against:
 *
 *   - the items after ১০ are renumbered — present occupation is ১১, education
 *     ১২, the applicant's certification ১৩, the enumerator ১৪ and the
 *     committee's decision ১৫;
 *   - item ৯ (present address) is printed at the foot of page 1, not the head
 *     of page 2;
 *   - there is no Election Commission emblem and no printing-press imprint;
 *   - the ফরম-২ number is one plain box, not a row of combed cells.
 *
 * The government file for this revision is a scan rather than vector text, so
 * unlike the previous revision nothing here could be read off PDF operators.
 * Every number below was measured off the deskewed scan instead: comb-cell
 * edges from the table's own hairline rules, label positions and dotted-leader
 * spans from the ink. The scanner's paper feed compressed the foot of each
 * page by a few points, so the vertical rhythm is regularised — the three
 * relative blocks of item ৮ and the two address blocks of items ৩ and ৭ are
 * laid out identically rather than reproducing the scan's drift.
 *
 * The preview re-typesets the form at these coordinates rather than stamping
 * the original, so a printed page lines up with the real form.
 */

/** Page box — A4 at 72 dpi. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/**
 * Font sizes (px = pt) and weights.
 *
 * The original is set in SutonnyMJ, which Kalpurush does not match in width, so
 * the body size is solved from the measured ink instead: each printed label's
 * width divided by Kalpurush's own advance for the same words. Four labels that
 * end in text rather than dots — so their true width is unambiguous — agree on
 * 8.9–9.0 pt. That is what keeps the colon stops and leader starts below on
 * their measured x instead of overrunning them.
 */
export const TYPE = {
    /** The title is the one line the original sets bold. */
    title: { size: 9.3, weight: 700 },
    body: { size: 8.95, weight: 400 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/**
 * Kalpurush's ascent is 1 em and its descent 0.4 em, so in that line box the
 * baseline lands 1.05 em below the top — the number that turns a measured
 * baseline into a CSS `top`.
 */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/**
 * The leaders are real full-stops in the form's own font — not a CSS border.
 *
 * They are the one place this sheet does not follow its own measurements. The
 * original prints them in the body size at a 2.623 pt pitch, but Kalpurush's
 * period inks only 0.115 em, so at 8.95 pt each dot is barely 1 pt across. The
 * export is a raster, and a viewer showing it at 100 % throws away two thirds
 * of every pixel row: dots that small come back as a grey haze or vanish
 * outright, which is what made the downloaded form look like it had no dotted
 * lines at all.
 *
 * So the dots get their own, larger glyph size and a slightly wider pitch —
 * the same 3.05 pt the Form-2 sheet uses, whose export has always been legible
 * — while everything else keeps the size solved from the printed ink. The
 * tracking is the pitch less the period's own 0.229 em advance.
 */
export const LEADER = {
    char: '.',
    /** Enough dots to overflow the longest line; each run is clipped. */
    count: 200,
    /** Bigger than the body text, so a dot still covers a pixel at 100 %. */
    size: 12,
    pitch: 3.05,
    letterSpacing: 3.05 - 12 * 0.229,
};

/**
 * The typed answers are not part of the printed form, so they do not have to
 * match its face: they print larger and bolder than the labels to stay legible
 * on a photocopy. A value that would overrun its blank shrinks down toward
 * `minSize` instead of being clipped.
 */
export const VALUE_TYPE = { size: 10, minSize: 7.2, weight: 400 };

/** The same, for the digits that print one per comb cell. */
export const CELL_VALUE_TYPE = { size: 10.5, minSize: 8, weight: 400 };

/**
 * Typed values sit above the dots, the way handwriting does — far enough that a
 * Latin descender (g, j, p, q, y) clears the leader instead of sitting on it.
 *
 * The leader dots ink 0.115 em above the baseline, which at `LEADER.size` puts
 * their tops 1.4 pt up. A descender drops about 2.1 pt below the value's own
 * baseline, so the lift has to beat the two together — 3 pt left the tails
 * crossing the dots, and made the answer look like it was sitting in the line
 * rather than on it.
 *
 * It cannot grow without limit: this revision sets some rows barely 11 pt below
 * the combed row above them — নিয়োগকারীর নাম under item ১০'s expiry boxes is
 * the tightest — so the lift plus the value's own ascent has to fit in that gap,
 * or an answer prints over the rules above it.
 */
export const VALUE_LIFT = 4.5;

/** …and start a few points in from the label, not hard against it. */
export const VALUE_INSET = 5;

/**
 * Where a comb cell's digit sits inside its box: centred, which for this box
 * height puts the digit baseline about two thirds of the way down.
 *
 * The row's own label is not centred — measured against nine combed rows it
 * sits at 0.47, its ascender level with the top rule — so `COMB_LABEL_RATIO`
 * is what turns a row's baseline into the top of the boxes on it.
 */
export const CELL_BASELINE_RATIO = 0.67;
export const COMB_LABEL_RATIO = 0.47;

/** Every combed row on the sheet is ruled to the same height. */
export const COMB_HEIGHT = 19.2;

/** Page 1 masthead: the form's own title and the ফরম-২ number line. */
export const PAGE1_HEAD = {
    /** The original centres this over neither the page nor the text column. */
    title: { y: 70, x: 156.7 },
    formNoLabel: { y: 82, x: 360.5 },
};

/**
 * The ফরম-২ number goes in one plain box — the only ruled field on the sheet
 * that is not divided into cells, so it takes free text rather than one digit
 * per box.
 */
export const FORM_NO_BOX = {
    top: 73,
    height: COMB_HEIGHT,
    left: 421.1,
    right: 522.3,
    value: 'formNo',
    /**
     * How long a number the box actually holds, which is what the editor caps
     * the field at — there is no cell count to take it from the way the combed
     * fields have one.
     *
     * 101.2 pt wide less `VALUE_INSET` at each end leaves 91.2 pt of writing
     * room, and Kalpurush's widest Bengali digit advances 0.598 em, so at
     * `VALUE_TYPE.size` every digit costs 5.98 pt: fifteen of them fill the box
     * and a sixteenth would not fit.
     */
    maxLength: 15,
};

/**
 * Every printed line of page 1: its baseline, the label that starts it, and —
 * where the form leaves a dotted blank — the span of that blank and the field
 * it takes.
 *
 * A `from` is where the dotted run starts. Rather than eyeballing it off the
 * scan, each one is the label's own measured width in Kalpurush plus 3 pt, so
 * no label can print into its own leader at any size the type is set at.
 *
 * `valueTo`, where a row carries one, is where the *answer* has to stop — short
 * of `to`, which is where the form's own dots stop. A few of the blanks run on
 * past the point where the row above starts its boxes, and an answer prints
 * above its line, so without this a long one would climb into those boxes.
 */
export const PAGE1_ROWS = [
    { y: 124, key: 'nameLabel', x: 70, from: 127.8, to: 474, value: 'nameBn' },
    { y: 142, key: 'fatherNameLabel', x: 70, from: 143.8, to: 472.6, value: 'fatherName' },
    { y: 160, key: 'fatherAddressLabel', x: 70, from: 194.4, to: 268.5, value: 'fDivision' },
    { y: 160, key: 'districtLabel', x: 272, from: 293.1, to: 361, value: 'fDistrict' },
    { y: 160, key: 'upazilaLabel', x: 365, from: 414.6, to: 473.5, value: 'fUpazila' },
    { y: 178, key: 'localBodyLabel', x: 83.5, from: 243, to: 435.9, value: 'fLocalBody' },
    { y: 178, key: 'wardNoLabel', x: 440 },
    { y: 196, key: 'mouzaLabel', x: 83.5, from: 106.3, to: 241.5, value: 'fMouza' },
    { y: 196, key: 'villageLabel', x: 245, from: 349.6, to: 509.3, valueTo: 498, value: 'fVillage' },
    { y: 214, key: 'holdingLabel', x: 83.5, from: 144.4, to: 250, value: 'fHolding' },
    { y: 214, key: 'postOfficeLabel', x: 254, from: 281.8, to: 396, value: 'fPostOffice' },
    { y: 214, key: 'postCodeLabel', x: 400 },
    { y: 232, key: 'guardianLabel', x: 70, from: 329.6, to: 483.9, valueTo: 452, value: 'guardianName' },
    { y: 251, key: 'nidLabel', x: 88 },
    { y: 251, key: 'relationLabel', x: 440, from: 465.3, to: 528, value: 'guardianRelation' },
    { y: 284, key: 'birthDateLabel', x: 88 },
    { y: 317, key: 'grandfatherNameLabel', x: 70, from: 133.9, to: 484.4, value: 'grandfatherName' },
    { y: 336, key: 'grandfatherNidLabel', x: 70 },
    { y: 336, key: 'notApplicableLabel', x: 472 },
    { y: 369, key: 'grandfatherAddressLabel', x: 70, from: 183.8, to: 260.8, value: 'gDivision' },
    { y: 369, key: 'districtLabel', x: 264.5, from: 285.6, to: 354, value: 'gDistrict' },
    { y: 369, key: 'upazilaLabel', x: 358, from: 407.6, to: 481.3, value: 'gUpazila' },
    { y: 387, key: 'localBodyLabel', x: 83.5, from: 243, to: 435.9, value: 'gLocalBody' },
    { y: 387, key: 'wardNoLabel', x: 440 },
    { y: 405, key: 'mouzaLabel', x: 83.5, from: 106.3, to: 241.5, value: 'gMouza' },
    { y: 405, key: 'villageLabel', x: 245, from: 349.6, to: 509.3, valueTo: 498, value: 'gVillage' },
    { y: 423, key: 'holdingLabel', x: 83.5, from: 144.4, to: 250, value: 'gHolding' },
    { y: 423, key: 'postOfficeLabel', x: 254, from: 281.8, to: 396, value: 'gPostOffice' },
    { y: 423, key: 'postCodeLabel', x: 400 },
    { y: 443, key: 'relativesHead', x: 70 },
    { y: 460, key: 'rel1Label', x: 70, from: 101.5, to: 488.5, value: 'rel1Name' },
    { y: 478, key: 'nidLabel', x: 88 },
    { y: 478, key: 'relationLabel', x: 440, from: 465.3, to: 527, value: 'rel1Relation' },
    { y: 512, key: 'birthDateLabel', x: 88 },
    { y: 512, key: 'mobileLabel', x: 308, from: 385.4, to: 521.9, value: 'rel1Mobile' },
    { y: 542, key: 'rel2Label', x: 70, from: 100.3, to: 488.5, value: 'rel2Name' },
    { y: 560, key: 'nidLabel', x: 88 },
    { y: 560, key: 'relationLabel', x: 440, from: 465.3, to: 527, value: 'rel2Relation' },
    { y: 594, key: 'birthDateLabel', x: 88 },
    { y: 594, key: 'mobileLabel', x: 308, from: 385.4, to: 521.9, value: 'rel2Mobile' },
    { y: 624, key: 'rel3Label', x: 70, from: 100.4, to: 488.5, value: 'rel3Name' },
    { y: 642, key: 'nidLabel', x: 88 },
    { y: 642, key: 'relationLabel', x: 440, from: 465.3, to: 527, value: 'rel3Relation' },
    { y: 676, key: 'birthDateLabel', x: 88 },
    { y: 676, key: 'mobileLabel', x: 308, from: 385.4, to: 521.9, value: 'rel3Mobile' },
    { y: 706, key: 'residenceLabel', x: 70 },
    { y: 706, key: 'ownLabel', x: 209 },
    { y: 706, key: 'rentLabel', x: 313 },
    { y: 706, key: 'otherLabel', x: 383 },
    { y: 727, key: 'ownNote', x: 83.5 },
    { y: 748, key: 'rentPeriodLabel', x: 83.5 },
    { y: 748, key: 'fromLabel', x: 366 },
    { y: 748, key: 'untilLabel', x: 520 },
];

/**
 * Page 1's comb rows. `edges` are the vertical rules of the printed table, so
 * a group of n cells carries n + 1 of them and the boxes are drawn between
 * neighbours. Each group's span is the one measured off the scan, divided
 * evenly — the original's own cells wander by a point either way, which is the
 * paper and the scanner rather than the form's intent.
 */
export const PAGE1_COMBS = [
    { top: 169, height: 19.2, edges: [502, 521.5, 541], value: 'fWardNo' },
    { top: 205, height: 19.2, edges: [455.5, 476.9, 498.2, 519.5, 540.9], value: 'fPostCode' },
    { top: 242, height: 19.2, edges: [135.3, 152.8, 170.3, 187.8, 205.3, 222.9, 240.4, 257.9, 275.4, 292.9, 310.4, 327.9, 345.4, 363, 380.5, 398, 415.5, 433], value: 'guardianNid' },
    { top: 275, height: 19.2, edges: [134.8, 153.4, 171.9, 190.4, 209, 227.6, 246.1, 264.6, 283.2], value: 'guardianBirthDate' },
    { top: 327, height: 19.2, edges: [166.1, 183.9, 201.7, 219.5, 237.3, 255.1, 272.9, 290.7, 308.5, 326.4, 344.2, 362, 379.8, 397.6, 415.4, 433.2, 451, 468.8], value: 'grandfatherNid' },
    { top: 327, height: 19.2, edges: [524.6, 546], value: 'grandfatherNidNa', tick: true },
    { top: 378, height: 19.2, edges: [502, 521.5, 541], value: 'gWardNo' },
    { top: 414, height: 19.2, edges: [455.5, 476.9, 498.2, 519.5, 540.9], value: 'gPostCode' },
    { top: 469, height: 19.2, edges: [132, 149.2, 166.4, 183.6, 200.8, 218, 235.2, 252.4, 269.6, 286.9, 304.1, 321.3, 338.5, 355.7, 372.9, 390.1, 407.3, 424.5], value: 'rel1Nid' },
    { top: 503, height: 19.2, edges: [137, 157.8, 178.7, 199.5, 220.3, 241.1, 262, 282.8, 303.6], value: 'rel1BirthDate' },
    { top: 551, height: 19.2, edges: [132, 149.2, 166.4, 183.6, 200.8, 218, 235.2, 252.4, 269.6, 286.9, 304.1, 321.3, 338.5, 355.7, 372.9, 390.1, 407.3, 424.5], value: 'rel2Nid' },
    { top: 585, height: 19.2, edges: [137, 157.8, 178.7, 199.5, 220.3, 241.1, 262, 282.8, 303.6], value: 'rel2BirthDate' },
    { top: 633, height: 19.2, edges: [132, 149.2, 166.4, 183.6, 200.8, 218, 235.2, 252.4, 269.6, 286.9, 304.1, 321.3, 338.5, 355.7, 372.9, 390.1, 407.3, 424.5], value: 'rel3Nid' },
    { top: 667, height: 19.2, edges: [137, 157.8, 178.7, 199.5, 220.3, 241.1, 262, 282.8, 303.6], value: 'rel3BirthDate' },
    { top: 739, height: 19.2, edges: [215.1, 233.4, 251.7, 270, 288.3, 306.6, 324.9, 343.2, 361.5], value: 'rentFrom' },
    { top: 739, height: 19.2, edges: [391.4, 407, 422.5, 438.1, 453.7, 469.3, 484.9, 500.4, 516], value: 'rentTo' },
];

/**
 * Item ৯'s three tick boxes, at the foot of page 1. They are not a comb — each
 * is its own square sitting just before the tenure it stands for.
 */
export const RESIDENCE_BOXES = [
    { left: 182.6, width: 21, top: 697, height: COMB_HEIGHT, choice: 'own' },
    { left: 289, width: 21, top: 697, height: COMB_HEIGHT, choice: 'rent' },
    { left: 358.5, width: 21, top: 697, height: COMB_HEIGHT, choice: 'other' },
];

/** Page 2 — the rest of item ৯, then items ১০ to ১৫. */
export const PAGE2_ROWS = [
    { y: 92.6, key: 'ownerNameLabel', x: 107.4, from: 176.1, to: 518.9, value: 'rentOwnerName' },
    { y: 110.3, key: 'ownerNidLabel', x: 107.4 },
    { y: 135.5, key: 'otherCaseLabel', x: 78, from: 208.2, to: 491.7, value: 'otherOwnerName' },
    { y: 153.6, key: 'ownerNidLabel', x: 107.4 },
    { y: 175.8, key: 'stayPeriodLabel', x: 91 },
    { y: 175.8, key: 'fromLabel', x: 341 },
    { y: 175.8, key: 'untilLabel', x: 523 },
    { y: 197.2, key: 'foreignBornNote', x: 78 },
    { y: 218.7, key: 'arrivalLabel', x: 91 },
    { y: 218.7, key: 'passportIssueLabel', x: 305 },
    { y: 244, key: 'issuePlaceLabel', x: 91, from: 136.2, to: 338.4, value: 'passportIssuePlace' },
    { y: 244, key: 'expiryLabel', x: 343 },
    // Nudged 1.4 pt below the measured baseline: the original leaves barely
    // 10 pt between item ১০'s expiry boxes and this row, too little for a
    // typed answer to sit above its dots without touching the rules above.
    { y: 269, key: 'jobJoinLabel', x: 78 },
    { y: 269, key: 'employerNameLabel', x: 361, from: 420.7, to: 518.6, value: 'employerName' },
    { y: 294, key: 'employerAddressLabel', x: 95.6, from: 164.4, to: 527.3, value: 'employerAddress' },
    { y: 311.5, from: 96.3, to: 387.6, value: 'employerAddress2' },
    { y: 311.5, key: 'employerMobileLabel', x: 391, from: 425.3, to: 530, value: 'employerMobile' },
    { y: 329, key: 'eduHead', x: 78 },
    { y: 347, key: 'edu1Label', x: 91, from: 209.8, to: 525.6, value: 'edu1Name' },
    { y: 365, key: 'studyPeriodLabel', x: 108.7, from: 148.9, to: 232.4, value: 'edu1From' },
    { y: 365, key: 'fromLabel', x: 236, from: 257.1, to: 326.4, value: 'edu1To' },
    { y: 365, key: 'eduResultLabel', x: 330, from: 432.7, to: 524.4, value: 'edu1Result' },
    { y: 383, key: 'edu2Label', x: 91, from: 208.6, to: 518.9, value: 'edu2Name' },
    { y: 401, key: 'studyPeriodLabel', x: 108.7, from: 148.9, to: 232.2, value: 'edu2From' },
    { y: 401, key: 'fromLabel', x: 236, from: 257.1, to: 329, value: 'edu2To' },
    { y: 401, key: 'eduResultLabel', x: 330, from: 432.7, to: 521.5, value: 'edu2Result' },
    { y: 418, key: 'certifyLabel', x: 78 },
    // The applicant signs on the line under the certification, not beside it.
    { y: 429.5, key: 'certifySignatureLabel', x: 95.6, from: 146.7, to: 173.2 },
    { y: 448, key: 'collectorNidLabel', x: 78 },
    { y: 448, key: 'signatureLabel', x: 450, from: 473.5, to: 531.9 },
    { y: 480, key: 'committeeLabel', x: 78 },
    { y: 517.5, key: 'memberSecretary', x: 78.8 },
    { y: 517.5, key: 'convener', x: 444.4 },
];

export const PAGE2_COMBS = [
    { top: 101.3, height: 19.2, edges: [208.1, 227.4, 246.7, 266, 285.3, 304.5, 323.8, 343.1, 362.4, 381.7, 401, 420.3, 439.6, 458.8, 478.1, 497.4, 516.7, 536], value: 'rentOwnerNid' },
    { top: 144.6, height: 19.2, edges: [208.1, 227.4, 246.7, 266, 285.3, 304.5, 323.8, 343.1, 362.4, 381.7, 401, 420.3, 439.6, 458.8, 478.1, 497.4, 516.7, 536], value: 'otherOwnerNid' },
    { top: 166.8, height: 19.2, edges: [155.6, 178.2, 200.8, 223.4, 246, 268.6, 291.2, 313.8, 336.4], value: 'otherFrom' },
    { top: 166.8, height: 19.2, edges: [367.2, 386.1, 405, 423.9, 442.9, 461.8, 480.7, 499.6, 518.5], value: 'otherTo' },
    { top: 209.7, height: 19.2, edges: [208.8, 220.2, 231.5, 242.9, 254.2, 265.6, 277, 288.3, 299.7], value: 'arrivalDate' },
    { top: 209.7, height: 19.2, edges: [434.2, 447.1, 460, 472.9, 485.8, 498.7, 511.6, 524.5, 537.4], value: 'passportIssueDate' },
    { top: 235, height: 19.2, edges: [424.1, 436.7, 449.3, 461.9, 474.5, 487.1, 499.7, 512.3, 524.9], value: 'passportExpiryDate' },
    { top: 260, height: 19.2, edges: [217.7, 235, 252.3, 269.7, 287, 304.3, 321.6, 339, 356.3], value: 'jobJoinDate' },
    { top: 439, height: 19.2, edges: [195.3, 210, 224.7, 239.4, 254.1, 268.9, 283.6, 298.3, 313, 327.7, 342.4, 357.1, 371.8, 386.6, 401.3, 416, 430.7, 445.4], value: 'collectorNid' },
];

/** The two solid rules the committee signs above. */
export const PAGE2_FOOT = {
    rules: [
        { y: 507, from: 73.5, to: 203.8 },
        { y: 507, from: 421.5, to: 536.9 },
    ],
};

/** Where each answer prints, so the preview can follow the field being typed. */
export const FIELD_ANCHORS = buildFieldAnchors([
    {
        rows: PAGE1_ROWS,
        combs: [
            FORM_NO_BOX,
            ...PAGE1_COMBS,
            // The tenure ticks are three separate boxes rather than a comb, so
            // the choice gets its anchor from the row they sit on.
            { value: 'residenceType', top: RESIDENCE_BOXES[0].top, height: COMB_HEIGHT },
        ],
    },
    { rows: PAGE2_ROWS, combs: PAGE2_COMBS },
]);
